import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { Operator } from '../api/generated';
import { formatValue } from '../format/valueFormatters';
import { defineStyles, theme } from '../theme';
import { AndChip, OrChip } from './FilterLogicChips';
const operatorToSymbol = {
    [Operator.Equals]: '=',
    [Operator.NotEquals]: '≠',
    [Operator.LessThan]: '<',
    [Operator.LessThanOrEqual]: '≤',
    [Operator.GreaterThan]: '>',
    [Operator.GreaterThanOrEqual]: '≥',
    [Operator.Contains]: '⊃',
    [Operator.NotContains]: '⊅',
    [Operator.StartsWith]: '…',
};
function getOperatorSymbol(condition) {
    const op = condition.operator;
    return operatorToSymbol[op] ?? op ?? '';
}
function getConditionValue(condition, filterColumns) {
    if (condition.operator === Operator.Empty ||
        condition.operator === Operator.NotEmpty ||
        condition.operator === Operator.Duplicity) {
        return '';
    }
    if (!('value' in condition) || condition.value == null)
        return '';
    const column = filterColumns.find((c) => c.fieldName === condition.field);
    return formatValue(condition.value, column?.format);
}
const styles = defineStyles({
    conditionChip: {
        borderRadius: 1,
        border: `1px solid ${theme.vars.palette.border.main}`,
        backgroundColor: theme.vars.palette.grey[50],
    },
    fieldName: {
        color: theme.vars.palette.primary.main,
        fontWeight: 600,
    },
    operator: {
        color: theme.vars.palette.text.secondary,
        fontWeight: 500,
    },
    value: {
        color: theme.vars.palette.text.secondary,
        fontWeight: 600,
    },
    editButton: {
        minHeight: 24,
        height: 24,
        py: 0,
        px: 1.5,
    },
    orGroupWrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
    },
    parenthesis: {
        color: theme.vars.palette.text.secondary,
        fontWeight: 500,
    },
});
export function FilterChipsSummary({ conditions, filterColumns, onRemoveCondition, }) {
    const andConditions = conditions.andConditions ?? [];
    const getFieldLabel = (fieldName) => {
        const col = filterColumns.find((c) => c.fieldName === fieldName);
        return col ? (typeof col.header === 'string' ? col.header : fieldName) : fieldName;
    };
    const andGroupNodes = andConditions.flatMap((andGroup, andIndex) => {
        const orConditions = andGroup.orConditions ?? [];
        const showParentheses = orConditions.length > 1;
        const orGroupContent = orConditions.flatMap((condition, orIndex) => {
            const orChip = orIndex > 0 ? _jsx(OrChip, {}, `or-${andIndex}-${orIndex}`) : null;
            const fieldLabel = getFieldLabel(condition.field);
            const operatorSymbol = getOperatorSymbol(condition);
            const value = getConditionValue(condition, filterColumns);
            const conditionChip = (_jsx(Chip, { size: "small", sx: styles.conditionChip, label: _jsxs("span", { children: [_jsx(Typography, { component: "span", variant: "body2", sx: styles.fieldName, children: fieldLabel }), _jsxs(Typography, { component: "span", variant: "body2", sx: styles.operator, children: [' ', operatorSymbol] }), _jsxs(Typography, { component: "span", variant: "body2", sx: styles.value, children: [' ', value] })] }), deleteIcon: _jsx(Icon, { path: mdiClose, size: 0.8 }), onDelete: () => onRemoveCondition(andIndex, orIndex) }, `cond-${andIndex}-${orIndex}`));
            return orIndex > 0 ? [orChip, conditionChip] : [conditionChip];
        });
        const groupContent = showParentheses
            ? [
                _jsx(Typography, { component: "span", sx: styles.parenthesis, children: "(" }, "openParenthesis"),
                ...orGroupContent,
                _jsx(Typography, { component: "span", sx: styles.parenthesis, children: ")" }, "closeParenthesis"),
            ]
            : orGroupContent;
        const andChip = andIndex > 0 ? _jsx(AndChip, {}, `and-${andIndex}`) : null;
        const groupBox = (_jsx(Box, { component: "span", sx: styles.orGroupWrapper, children: groupContent }, `group-${andIndex}`));
        return andIndex > 0 ? [andChip, groupBox] : [groupBox];
    });
    return (andGroupNodes ?? null);
}
//# sourceMappingURL=FilterChipsSummary.js.map